<style lang="scss" scoped>
    .module-section {
        font-size: 18px;

        .section-name {
            color: #4d8ab7;
            font-weight: 300;
            font-size: 1.1em;
        }

        .section-contact-hours {
            color: #223264;
            font-size: 1.0em;
        }

        .handout-link {
            font-size: 1.0em;
        }
    }

    .modal-training-title {
        font-size: 2.0em;
        color: #fff;
    }

    .training-hr {
        margin-top: 5px;
        margin-bottom: 0.8em !important;
    }

    .font-size-3em {
        font-size: 3.0em;
    }

    .yanone {
        font-family: "Yanone Kaffeesatz", sans-serif;
    }

    .start-menu {
        height: 40px;;
    }

    .completed-training:hover {
        background-color: #ffe3e0;
        cursor: pointer;
    }

    .completed-training-instructions {
        color: #4d8ab7 !important;
    }
</style>

<template>
    <div>
        <BModal id="content-modal" ref="contentModal" v-model="isContentModalVisible"
            @hidden="onContentModalHidden"
            :header-class="'bg-'+moduleColor" 
            centered
            size="xl"
            hide-footer
            :static=true
        >
            <template #modal-header>
              <span class="modal-training-title yanone">{{ trainingTitle }}</span>
              <BCloseButton class="font-size-3em" @click="$bvModal.hide('content-modal')" />
            </template>

            <template #default>
              <component :is="currentComponent"
                         :training-session-id="currentSessionId"
                         :training-id="currentTrainingId"
                         :is-dev="isDev"
              ></component>
            </template>
        </BModal>

      <section id="" class="mb-5">
        <h2 class="lighter text-primary" id="landing">Get Ready to Learn More</h2>
        <how-to-row>
          <template v-slot:left>
            <div class="how-to-guide">
              <a :href="$basePath+'/uploads/provider_trainings_page_how_to_guide_branded.pdf'" target="_blank"><span class="icon-book"></span></a>
              <a :href="$basePath+'/uploads/provider_trainings_page_how_to_guide_branded.pdf'" target="_blank" class="link-lighter">View the How-To Guide</a>
            </div>
          </template>
        </how-to-row>
      </section>
        <dashboard-tabs :modules="modules" :active-index=0>
            <template v-slot:primary="{ module }">
              <div v-if="hasRole('ta-consultant')" class="font-weight-bold container text-center h-100 pt-3">
                <p>Please click on the name of a training session below to view and print each participant's Certificate of Training Completion.</p>
              </div>
              <div v-else class='text-center font-weight-bold pt-3'>
                <p>Click <span class='text-orange'>Start</span> by any section image to start training session. After naming your session, click <span class='text-orange'>Start</span> again to access your training session video, quiz, and evaluation.</p>
                <p>To receive a <strong>Certificate of Training Completion</strong>: view the full training, pass the knowledge quiz, and complete the training evaluation. Groups viewing the training together will complete one quiz and one evaluation but have the option to print individual certificates.</p>
              </div>
            </template>

            <template v-slot:secondary="{ module }">
                    <BContainer v-if="!hasRole('ta-consultant')" :class="'mx-0 p-4 border-'+ module.color +' border-top-0'">
                        <BRow cols="2">
                            <BCol v-for="training in trainingsInModule(module.id)" :key="training.id" class="module-section pb-5">
                                <BRow>
                                    <BCol cols="4">
                                        <div class="section-image img-wrapper-green">
                                            <img :src="getTrainingImageURL(training.image_thumb)" class="w-100" />
                                        </div>
                                    </BCol>
                                    <BCol cols="8">
                                        <div class="section-name yanone">{{training.title}}</div>
                                        <hr class="training-hr" />
                                        <div class="section-contact-hours">{{ training.contact_hours }} Contact Hours</div>
                                        <a :href="'uploads/trainings/handouts/' + module.slug + '/' + training.handout_file" class="handout-link text-orange font-weight-bold" target="_blank">Print Handout</a>
                                        <training-start-menu
                                            class="start-menu"
                                            :training-id="training.id"
                                            @select-content="setCurrentContent"
                                        ></training-start-menu>
                                    </BCol>
                                </BRow>
                            </BCol>
                        </BRow>
                    </BContainer>

                    <!-- ------------------------------ -->
                    <!-- COMPLETED TRAININGS SECTION -->
                    <!-- ------------------------------ -->
                    <div :class="'completed-trainings row container mx-0 p-4 border-'+ module.color +' border-top-0'"
                    >
                        <div class="col-12">
                            <h2 class="landing mb-4" id="landing">Completed Training Sessions</h2>
                            <h3 class='completed-training-instructions'>Click on session name to print your Certificate of Training Completion.</h3>

                            <div class="icon-name-view row mx-0 py-3 px-3">
                                <div class="col-sm-3 p-0 text-sm-left align-middle"><span class="module-status-link">Training</span></div>
                                <div class="col-sm-3 p-0 text-sm-left align-middle"><span class="module-status-link">Session</span></div>
                                <div class="col-sm-2 p-0 text-sm-left align-middle"><span class="module-status-link">Total Participants</span></div>
                                <div class="col-sm-2 p-0 text-sm-right align-middle"><span class="module-status-link">Date Started</span></div>
                                <div class="col-sm-2 p-0 text-sm-right align-middle"><span class="module-status-link">Date Completed</span></div>
                            </div>
                            <!-- LOOP THROUGH COMPLETED SESSIONS -->
                            <template v-for="(session, sIdx) in completedSessionsInModule(module)" :key="session.id">
                                <div class="icon-name-view completed-training row mx-0 py-3 px-3" :class="sIdx >= 3 ? completedSessionClasses : ''" @click="onClickCompletedSession(session.training_id, session.id)">
                                    <div class="col-sm-3 p-0 text-sm-left align-middle">
                                        <module-icon :module-slug="module.slug" size="md" class="module-icon"></module-icon>
                                        <span class="module-name">{{ session.training.title }}</span>
                                    </div>
                                    <div class="col-sm-3 p-0 text-sm-left align-middle">
                                        <span class="module-status-link" v-html="truncateTitle(session.title, 20)"></span>
                                    </div>
                                    <div class="col-sm-2 p-0 text-sm-left align-middle">
                                        <span class="module-status-link">{{ getSessionParticipantCount(session) || session.participant_count }}</span>
                                    </div>
                                    <div class="col-sm-2 p-0 text-sm-right align-middle">
                                        <span class="module-status-link">{{  formatDate(session.start_time) }}</span>
                                    </div>
                                    <div class="col-sm-2 p-0 text-sm-right align-middle">
                                        <span class="module-status-link">{{  formatDate(session.finalized_at) }}</span>
                                    </div>
                                </div><!-- .row -->
                            </template>
                        </div><!-- .col-12 -->
                        <div class="col-12">
                            <button @click="isCompletedSessionsExpanded = !isCompletedSessionsExpanded" class="btn bg-orange text-white float-right" data-toggle="collapse" data-target=".session-collapse-wrapper" aria-expanded="false" aria-controls="session-collapse-wrapper">{{ expandButtonText }}</button>
                        </div>
                    </div>
            
            </template>
        </dashboard-tabs>
    </div>
</template>

<script>
    import ModuleIcon from '../ModuleIcon';
    import DashboardTabs from '../dashboard_tabs/DashboardTabs';
    import moment from 'moment';
    import HeelstreamVideoEmbed from './HeelstreamVideoEmbed';
    import PanoptoVideoEmbed from './PanoptoVideoEmbed';
    import ProviderTrainingKnowledgeQuiz from './ProviderTrainingKnowledgeQuiz';
    import ProviderTrainingEvaluation from './ProviderTrainingEvaluation';
    import ProviderTrainingCertificatePrint from './ProviderTrainingCertificatePrint';
    import TrainingStartMenu from './TrainingStartMenu';
    import { mapState, mapMutations, mapGetters } from 'vuex';
    import Auth from '../../auth.js';
    import HowToRow from '../../components/HowToRow';
    import { useStore } from 'vuex'; 


    export default{
        components: {
            ModuleIcon,
            DashboardTabs,
            moment,
            PanoptoVideoEmbed,
            'provider-training-knowledge-quiz': ProviderTrainingKnowledgeQuiz,
            'provider-training-evaluation': ProviderTrainingEvaluation,
            'provider-training-certificate-print': ProviderTrainingCertificatePrint,
            'training-start-menu': TrainingStartMenu,
            HowToRow,
            // SurveyPage,
        },
        props: {
            modules: {
                required: true,
                type: Array
            },
            isDev: {
                type: Boolean,
                default: false
            },
            programId: {
                required: false,
                type: Number,
                default: null,
            },
        },
        data() {
            return {
                currentComponent: null,
                currentTrainingId: null,
                currentSessionId: null,
                isCompletedSessionsExpanded: false,
                isContentModalVisible: false,
            }
        },
        methods: {
            getSessionParticipantCount(session) {
              const participants = this.$store.state.sessionParticipants[session.id];
              return participants ? participants.length : 0;
            },
            getTrainingImageURL(imageFilename) {
              return '/images/summary/' + imageFilename;
            },
            completedSessionsInModule(_module) {
                if(this.completedTrainingSessionsSorted) {
                    return this.completedTrainingSessionsSorted.filter((s, i) => {
                        if(s.module) {
                            return s.module.id == _module.id;
                        }
                    });
                }
            },
            setCurrentContent(component, trainingId, sessionId) {
                this.currentSessionId = sessionId;
                this.currentTrainingId = trainingId;
                this.currentComponent = component;
                this.isContentModalVisible = true;
                this.$refs.contentModal.show();
            },
            onContentModalHidden() {
                this.isContentModalVisible = false;
                this.resetContent();
            },
            resetContent() {
                this.currentComponent = null;
                this.currentSessionId = null;
                this.currentTrainingId = null;
            },
            initState() {
                let vm = this;

                axios.get('/provider-trainings/data' + (vm.programId ? "/program/" + vm.programId : ""))
                .then(response => {
                    let trainings = response.data.trainings; 
                    vm.initTrainings({trainings});
                    
                    let trainingSessions = response.data.trainingSessions;
                    vm.initTrainingSessions({trainingSessions});

                    let sessionParticipants = response.data.sessionParticipants;
                    vm.initSessionParticipants({sessionParticipants});

                    let latestQuizResponses = response.data.latestQuizResponses;
                    vm.initLatestQuizResponses({latestQuizResponses});

                    let latestEvaluationResponses = response.data.latestEvaluationResponses;
                    vm.initLatestEvaluationResponses({latestEvaluationResponses});
                });
            },
            onClickCompletedSession(trainingId, sessionId) {
                this.setCurrentContent('provider-training-certificate-print', trainingId, sessionId);
            },
            truncateTitle(title, length=30) {
                if(!title) {
                    title = "Unnamed Session";
                }
                if(title.length > length) {
                    return title.substring(0,length) + "&hellip;";
                }
                return title;
            },
            hasRole(role) {
                return Auth.is(role);
            },
            ...mapMutations({
                initTrainings: 'INIT_TRAININGS',
                initTrainingSessions: 'INIT_TRAINING_SESSIONS',
                initSessionParticipants: 'INIT_SESSION_PARTICIPANTS',
                initLatestQuizResponses: 'INIT_LATEST_QUIZ_RESPONSES',
                initLatestEvaluationResponses: 'INIT_LATEST_EVALUATION_RESPONSES',
            }),
            formatDate: function (value) {
                if (value) {
                    return moment(String(value)).format('MM/DD/YYYY');
                }
            }
        },
        computed: {
            $basePath() {
              return window.basePath;
            },
            currentTraining: function() {
                if(this.currentTrainingId) {
                    return this.$store.state.trainings[this.currentTrainingId];
                }
                return null;
            },
            currentSession: function() {
                if(this.currentSessionId) {
                    return this.$store.state.trainingSessions[this.currentSessionId];
                }
                return null;
            },
            expandButtonText: function() {
                return this.isCompletedSessionsExpanded ? "Collapse" : "Show All";
            },
            trainingTitle: function() {
                if(this.currentTraining) {
                    return this.currentTraining.title;
                }
                return "Current Training";
            },
            moduleColor() {
                if(this.currentTraining && this.currentTraining.section && this.currentTraining.section.module) {
                   return this.currentTraining.section.module.color;
                }
                return "white";
            },
            completedTrainingSessions() {
                return Object.values(this.$store.state.trainingSessions).filter((s,i) => {
                    return s.is_finalized;
                });
            },
            completedTrainingSessionsSorted() {
                return this.completedTrainingSessions.sort((a, b) => {
                    // sort oldest to newest
                    return (new Date(a.finalized_at)) - (new Date(b.finalized_at));
                }).reverse(); // sort newest to oldest
            },
            /**
             * Get classes that make a row collapsible
             */
            completedSessionClasses() {
                return {
                    collapse: true,
                    'session-collapse-wrapper': true,
                };
            },
            ...mapGetters([
                'trainingsInModule'
            ]),
        },
        mounted: function() {
            this.initState();
        }
    }
</script>
